import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { t } from '@/i18n';
import { cn } from '@shared/lib';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">{t('common.close')}</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

interface DialogWideProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Radix가 요구하는 접근성 제목(`DialogTitle`, sr-only) — 시각적으로는 안 보이지만
   * 스크린리더 사용자에게는 필수. 호출부가 이미 화면에 그리고 있는 제목 문구를 그대로
   * 넘긴다(새 문구를 짓지 않는다) — i18n 카탈로그 경유 필수, 하드코딩 금지.
   */
  title: string;
  children: React.ReactNode;
}

/**
 * TRAD-2099 W-13 — `components/modal/Modal.tsx`(TRAD-2113에서 이미 Radix Dialog로
 * 이관된 산출물) 흡수. 그 파일이 "생 오버레이"였던 적은 없다 — 남아 있던 진짜 중복은
 * 이 파일의 `DialogContent`(폭 lg·bg-background)와 시각 스펙이 다른 **두 번째 Dialog
 * 래퍼가 별도 파일로 공존**한 것이었다. 시각 스펙(폭 2xl·`bg-card`·텍스트 "×" 닫기,
 * 아래 클래스 문자열 전부 Modal.tsx 원문과 바이트 동일)은 그대로 옮기고 파일만
 * 하나로 합친다 — **시각 변화 0**(정본 판정: 클래스 집합 원문 대조, 2026-09-12).
 *
 * 소비처 3곳(MyPage 닉네임 변경 · feed-write-page 종목 검색 · AssetFormModal)이 전부
 * `isOpen`/`onClose`/`children`만 쓰고 Modal.tsx의 `searchBar` prop은 구현부 어디서도
 * 안 읽혀 죽은 코드였다 — 이 이관과 함께 소멸(새 API에 없음).
 *
 * 추가: 3곳 다 Radix `DialogTitle` 미연결 상태였다(기존 접근성 결함, 콘솔 a11y 경고
 * 대상 — Modal.test.tsx 옛 주석에도 "아직 소비처별 제목이 없어 경고가 뜬다"고 이미
 * 기록돼 있었다). `title` prop을 필수로 받아 `sr-only`로 연결해 그 결함을 이 이관과
 * 함께 없앤다 — 화면엔 아무것도 안 보인다(시각 변화 0 유지).
 *
 * 🔴 `relative`를 className에 넣지 않는다 — Modal.tsx 이력의 실측 함정: twMerge가
 * `fixed`/`relative`/`absolute`를 같은 "position" 충돌 그룹으로 보고 나중 것만 남겨,
 * `relative`를 뒤에 두면 `fixed`가 지워져 다이얼로그가 문서 흐름으로 떨어진다(jsdom은
 * 레이아웃을 안 그려서 못 잡는다 — 실브라우저에서만 드러남).
 */
export const DialogWide = ({ isOpen, onClose, title, children }: DialogWideProps) => (
  <Dialog
    open={isOpen}
    onOpenChange={open => {
      if (!open) onClose();
    }}
  >
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-4 right-4 top-1/2 z-50 mx-auto max-w-2xl -translate-y-1/2',
          'bg-card sm:px-6 px-4 rounded-lg shadow-lg max-h-[90vh] overflow-auto',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogClose className="absolute top-0 right-2 text-muted-foreground hover:text-foreground text-2xl">
          &times;
          <span className="sr-only">{t('common.close')}</span>
        </DialogClose>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  </Dialog>
);

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
